import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown/checking, false = anonymous, object = signed in
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    // Skip the auth probe on truly public routes (invite landing) to avoid cosmetic 401s
    const path = window.location.pathname || "";
    if (path.startsWith("/coborrower-invite/") || path.startsWith("/guarantor-invite/")) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Anonymous user — expected on public routes
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("Auth probe failed:", e.message);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) localStorage.setItem("cf_token", data.token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data?.token) localStorage.setItem("cf_token", data.token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout API failed (clearing local session anyway):", e.message); }
    localStorage.removeItem("cf_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function roleHomePath(role) {
  switch (role) {
    case "borrower": return "/borrower";
    case "dealer": return "/dealer";
    case "dsa": return "/dsa";
    case "underwriter": return "/underwriter";
    case "operations": return "/admin";
    case "collections": return "/collections";
    case "admin":
    case "super_admin":
      return "/admin";
    default: return "/";
  }
}
